// Self-service account management (#289). Available to every authenticated role; the server
// remains authoritative for profile validation, global email uniqueness, and session revocation.
// The password card lives in account-password-card.tsx (#382: shared with Settings).
import React from "react";
import { Avatar, Banner, Button, Card, FormField, Input, Switch } from "@/ds";
import { ApiError, cockpit } from "@/api";
import type { AccountProfile, SessionUser } from "@/api";
import { Icon, SectionLayout, SectionNav } from "@/lib";
import { useT } from "../i18n";
import { AccountPasswordCard } from "./account-password-card";
import type { PushToast } from "@/notifications/toast-queue";

export interface AccountScreenProps {
  user: SessionUser;
  onUserChanged: () => Promise<SessionUser | null>;
  pushToast: PushToast;
  notificationSoundsEnabled: boolean;
  onNotificationSoundsEnabledChange: (enabled: boolean) => void;
}

export function AccountScreen({
  user,
  onUserChanged,
  pushToast,
  notificationSoundsEnabled,
  onNotificationSoundsEnabledChange,
}: AccountScreenProps) {
  const t = useT();
  const [section, setSection] = React.useState("profile");
  const sections = [
    { id: "profile", label: t("account.profile") },
    { id: "notifications", label: t("account.notifications") },
    { id: "password", label: t("account.password") },
  ];
  const [profile, setProfile] = React.useState<AccountProfile | null>(null);
  const [name, setName] = React.useState(user.name ?? "");
  const [email, setEmail] = React.useState(user.email);
  const [profileError, setProfileError] = React.useState<string | null>(null);
  const [profileBusy, setProfileBusy] = React.useState(false);
  React.useEffect(() => {
    let alive = true;
    cockpit
      .getAccountProfile()
      .then((next) => {
        if (!alive) return;
        setProfile(next);
        setName(next.name ?? "");
        setEmail(next.email);
      })
      .catch((error: unknown) => {
        if (alive) setProfileError(error instanceof Error ? error.message : "");
      });
    return () => { alive = false; };
  }, []);

  const saveProfile = async () => {
    setProfileError(null);
    setProfileBusy(true);
    try {
      const next = await cockpit.updateAccountProfile({ email: email.trim(), name: name.trim() || null });
      setProfile(next);
      setName(next.name ?? "");
      setEmail(next.email);
      await onUserChanged();
      pushToast({ tone: "success", title: t("account.profileSaved") });
    } catch (error) {
      setProfileError(
        error instanceof ApiError && error.status === 409
          ? t("account.emailUnavailable")
          : error instanceof Error && error.message
            ? error.message
            : t("common.error"),
      );
    } finally {
      setProfileBusy(false);
    }
  };

  const identityEmail = profile?.email || user.email;
  const identityName = (profile?.name ?? user.name)?.trim() || identityEmail;

  return (
    <div className="page page--management page--account">
      <div className="page-head">
        <div>
          <h1>{t("account.title")}</h1>
          <p>{t("account.sub")}</p>
        </div>
      </div>

      <SectionLayout
        active={section}
        title={sections.find((entry) => entry.id === section)!.label}
        nav={<SectionNav entries={sections} active={section} onSelect={setSection} ariaLabel={t("account.sectionsAria")} />}
      >
        {/* Keep forms mounted for this visit; hidden panels leave the focus and accessibility trees. */}
        <section data-account-section="profile" hidden={section !== "profile"} aria-label={t("account.profile")}>
          <Card>
            <div className="account-identity">
              <Avatar name={identityName} sub={identityName === identityEmail ? undefined : identityEmail} size="lg"
                aria-hidden={identityName === identityEmail || undefined} />
              {identityName === identityEmail && <span className="neo-ident__name" style={{ minWidth: 0, overflowWrap: "anywhere" }}>{identityEmail}</span>}
            </div>
            {profileError && <Banner tone="danger" title={t("common.error")}>{profileError}</Banner>}
            {!profile && !profileError && <p className="subtle">{t("common.loading")}</p>}
            {profile && (
              <div className="stack-sm account-form">
                <FormField label={t("account.displayName")} htmlFor="account-display-name">
                  <Input
                    id="account-display-name"
                    value={name}
                    maxLength={80}
                    autoComplete="name"
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => setName(event.currentTarget.value)}
                  />
                </FormField>
                <FormField label={t("account.email")} htmlFor="account-email" required>
                  <Input
                    id="account-email"
                    type="email"
                    value={email}
                    autoComplete="email"
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => setEmail(event.currentTarget.value)}
                  />
                </FormField>
                <div className="management-actions">
                  <Button loading={profileBusy}
                    variant="primary"
                    iconLeft={<Icon name="check" size={16} />}
                    disabled={profileBusy || !email.trim()}
                    onClick={() => { void saveProfile(); }}
                  >
                    {t("account.saveProfile")}
                  </Button>
                </div>
              </div>
            )}
          </Card>
        </section>

        <section data-account-section="notifications" hidden={section !== "notifications"} aria-label={t("account.notifications")}>
          <Card>
            <div className="stack-sm">
              <p className="subtle management-copy">{t("account.notificationSoundsHint")}</p>
              <Switch
                checked={notificationSoundsEnabled}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  onNotificationSoundsEnabledChange(event.currentTarget.checked)}
                label={t("account.notificationSounds")}
                onLabel={t("shell.active")}
                offLabel={t("shell.off")}
              />
            </div>
          </Card>
        </section>

        <section data-account-section="password" hidden={section !== "password"} aria-label={t("account.password")}>
          <AccountPasswordCard pushToast={pushToast} />
        </section>
      </SectionLayout>
    </div>
  );
}
